/* =====================================================================
 * Component tokens — per-component spacing/typography bundles.
 *
 * Identical to the corresponding blocks in askyourai/src/theme/theme.ts
 * (markdown, markdownMobile, bubble, bubbleMobile, modal, modalMobile,
 *  input, inputMobile, dm, dmMobile, sidebar, sidebarMobile).
 *
 * Each pair (foo / fooMobile) follows the convention: desktop defaults
 * first, then mobile (<768px) overrides.
 * ===================================================================== */

/** Markdown renderer desktop tokens (Pretendard baseline). */
export const markdownTokens = {
  fontSize: '1rem',
  lineHeight: '1.65',
  h1: '1.5rem',
  h2: '1.3rem',
  h3: '1.15rem',
  headingLineHeight: '1.3',
  headingWeight: '600',
  codeFontSize: '0.8125rem',
  codeLineHeight: '1.55',
  codeHeaderPad: '7px 12px',
  codePad: '12px 16px',
  codePadLn: '12px 0 12px 7px',
  headerFontSize: '0.75rem',
  badgeFontSize: '0.7rem',
  inlineCodeFontSize: '0.875em',
  inlineCodePad: '2px 5px',
  tableFontSize: '0.8125rem',
  tableHeaderFontSize: '0.75rem',
  tableCellPad: '8px 14px',
  listPadLeft: '1.4em',
  iconSize: '12',
  bqPad: '0.4em 0.9em',
} as const;

/** Markdown mobile (<768px) overrides. */
export const markdownMobileTokens = {
  fontSize: '0.875rem',
  lineHeight: '1.55',
  h1: '1.125rem',
  h2: '1.05rem',
  h3: '0.9375rem',
  codeFontSize: '0.6875rem',
  codeHeaderPad: '4px 7px',
  codePad: '7px 10px',
  codePadLn: '7px 0 7px 3px',
  headerFontSize: '0.625rem',
  badgeFontSize: '0.6rem',
  inlineCodeFontSize: '0.775em',
  inlineCodePad: '1px 3px',
  tableFontSize: '0.7rem',
  tableHeaderFontSize: '0.625rem',
  tableCellPad: '5px 7px',
  listPadLeft: '1em',
  iconSize: '10',
  bqPad: '0.25em 0.5em',
} as const;

/** Message bubble (user/assistant rows) desktop tokens. */
export const bubbleTokens = {
  rowGap: '16px',
  rowPad: '20px 16px',
  userPad: '10px 20px',
  userFontSize: '1rem',
  userLineHeight: '1.65',
  userMaxWidth: '75%',
  assistantFontSize: '1rem',
  assistantLineHeight: '1.7',
  fileFontSize: '0.8125rem',
  fileSizeFontSize: '0.6875rem',
  fileIconSize: '36px',
  fileExtFontSize: '0.5rem',
  previewWidth: '180px',
  errorFontSize: '0.875rem',
  errorPad: '10px 14px',
  metaFontSize: '0.75rem',
  actionBtnPad: '6px',
  actionIconSize: '14',
} as const;

/** Bubble mobile (<768px) overrides. */
export const bubbleMobileTokens = {
  rowGap: '12px',
  rowPad: '14px 12px',
  userPad: '8px 14px',
  userFontSize: '0.9375rem',
  userLineHeight: '1.6',
  assistantFontSize: '0.9375rem',
  assistantLineHeight: '1.65',
  fileFontSize: '0.75rem',
  fileSizeFontSize: '0.625rem',
  fileIconSize: '30px',
  fileExtFontSize: '0.4375rem',
  previewWidth: '140px',
  errorFontSize: '0.8125rem',
  errorPad: '8px 10px',
  metaFontSize: '0.6875rem',
  actionBtnPad: '5px',
  actionIconSize: '12',
} as const;

/** Modal desktop tokens. */
export const modalTokens = {
  titleFontSize: '1.125rem',
  sectionTitleFontSize: '0.8125rem',
  sectionDescFontSize: '0.75rem',
  bodyFontSize: '0.875rem',
  smallFontSize: '0.75rem',
  badgeFontSize: '0.6875rem',
  labelFontSize: '0.8125rem',
  nameFontSize: '0.875rem',
  descFontSize: '0.75rem',
} as const;

/** Modal mobile overrides. */
export const modalMobileTokens = {
  titleFontSize: '1rem',
  sectionTitleFontSize: '0.75rem',
  sectionDescFontSize: '0.6875rem',
  bodyFontSize: '0.8125rem',
  smallFontSize: '0.6875rem',
  badgeFontSize: '0.625rem',
  labelFontSize: '0.75rem',
  nameFontSize: '0.8125rem',
  descFontSize: '0.6875rem',
} as const;

/** Message input box desktop tokens. */
export const inputTokens = {
  wrapperPad: '0 16px 16px',
  rowPad: '8px 8px 8px 16px',
  fontSize: '1rem',
  lineHeight: '1.5',
  textPad: '6px 0',
  iconBtnSize: '36px',
  attachGap: '8px',
  attachPad: '12px 16px 4px',
  filePad: '10px 32px 10px 10px',
  fileIconSize: '42px',
  fileFontSize: '0.8125rem',
  fileSizeFontSize: '0.6875rem',
  fileExtFontSize: '0.5625rem',
  hintFontSize: '0.75rem',
  hintPad: '4px 0 0',
  uploadFontSize: '0.8125rem',
} as const;

/** Message input mobile overrides. */
export const inputMobileTokens = {
  wrapperPad: '0 10px 10px',
  rowPad: '6px 6px 6px 12px',
  fontSize: '0.9375rem',
  lineHeight: '1.5',
  textPad: '5px 0',
  iconBtnSize: '32px',
  attachGap: '6px',
  attachPad: '10px 12px 4px',
  filePad: '8px 28px 8px 8px',
  fileIconSize: '36px',
  fileFontSize: '0.75rem',
  fileSizeFontSize: '0.625rem',
  fileExtFontSize: '0.5rem',
  hintFontSize: '0.6875rem',
  hintPad: '3px 0 0',
  uploadFontSize: '0.75rem',
} as const;

/** DM (direct message) desktop tokens — shares sidebar design language. */
export const dmTokens = {
  listWidth: '300px',
  listMinWidth: '260px',
  listMaxWidth: '340px',
  headerPad: '18px 16px',
  headerTitleFontSize: '0.8125rem',
  headerTitleWeight: '600',
  listPad: '0 8px',
  listItemPad: '8px 10px',
  listItemGap: '10px',
  listItemRadius: '8px',
  avatarSize: '28px',
  avatarFontSize: '11px',
  avatarFontWeight: '700',
  avatarDotSize: '7px',
  avatarDotBorder: '2px',
  nameFontSize: '0.875rem',
  nameWeight: '500',
  previewFontSize: '0.8125rem',
  timeFontSize: '0.6875rem',
  badgeSize: '18px',
  badgeFontSize: '0.625rem',
  threadMaxWidth: '680px',
  threadHeaderPad: '14px 20px',
  threadHeaderAvatarSize: '32px',
  threadHeaderAvatarFontSize: '12px',
  threadHeaderNameFontSize: '0.875rem',
  threadHeaderDescFontSize: '0.75rem',
  messagesPad: '16px 20px',
  messagesGap: '14px',
  msgAvatarSize: '28px',
  msgAvatarFontSize: '11px',
  bubbleMaxWidth: '75%',
  bubblePad: '10px 14px',
  bubbleFontSize: '0.875rem',
  bubbleLineHeight: '1.5',
  bubbleRadius: '18px',
  bubbleRadiusTail: '4px',
  metaFontSize: '0.6875rem',
  inputPad: '12px 20px',
  inputInnerPad: '10px 14px',
  inputFontSize: '0.875rem',
  inputRadius: '24px',
  inputSendSize: '34px',
  emptyAvatarSize: '56px',
  emptyAvatarFontSize: '20px',
  emptyTitleFontSize: '1.125rem',
  emptyDescFontSize: '0.8125rem',
} as const;

/** DM mobile overrides. */
export const dmMobileTokens = {
  listWidth: '100%',
  listMinWidth: '100%',
  listMaxWidth: '100%',
  headerPad: '10px 12px',
  headerTitleFontSize: '0.625rem',
  listPad: '0 6px',
  listItemPad: '7px 8px',
  listItemGap: '8px',
  listItemRadius: '8px',
  avatarSize: '28px',
  avatarFontSize: '11px',
  avatarDotSize: '7px',
  avatarDotBorder: '2px',
  nameFontSize: '0.8125rem',
  previewFontSize: '0.75rem',
  timeFontSize: '0.625rem',
  badgeSize: '16px',
  badgeFontSize: '0.5625rem',
  threadMaxWidth: '100%',
  threadHeaderPad: '10px 16px',
  threadHeaderAvatarSize: '28px',
  threadHeaderAvatarFontSize: '11px',
  threadHeaderNameFontSize: '0.8125rem',
  threadHeaderDescFontSize: '0.625rem',
  messagesPad: '12px 16px',
  messagesGap: '10px',
  msgAvatarSize: '24px',
  msgAvatarFontSize: '10px',
  bubbleMaxWidth: '85%',
  bubblePad: '8px 12px',
  bubbleFontSize: '0.8125rem',
  bubbleLineHeight: '1.45',
  bubbleRadius: '16px',
  bubbleRadiusTail: '4px',
  metaFontSize: '0.625rem',
  inputPad: '10px 16px',
  inputInnerPad: '8px 12px',
  inputFontSize: '0.8125rem',
  inputRadius: '22px',
  inputSendSize: '30px',
  emptyAvatarSize: '48px',
  emptyAvatarFontSize: '18px',
  emptyTitleFontSize: '1rem',
  emptyDescFontSize: '0.75rem',
} as const;

/** Sidebar desktop tokens. */
export const sidebarTokens = {
  headerHeight: '64px',
  headerPad: '0 12px',
  logoHeight: '28px',
  newChatFontSize: '0.875rem',
  newChatPad: '10px 16px',
  newChatGap: '10px',
  sectionFontSize: '0.8125rem',
  sectionPad: '14px 16px 6px',
  pageSectionPad: '10px 10px 4px',
  pageGroupTitlePad: '12px 10px 6px',
  sectionListPad: '0 8px',
  itemFontSize: '0.875rem',
  itemPad: '8px 10px',
  itemGap: '10px',
  itemIconSize: '16',
  groupTitleFontSize: '0.6875rem',
  groupTitlePad: '16px 8px 6px',
  historyFontSize: '0.875rem',
  historyPad: '8px 10px',
  historyGap: '8px',
  historyIconSize: '16',
  footerPad: '8px 12px 12px',
  profilePad: '8px',
  profileGap: '10px',
  profileNameFontSize: '0.8125rem',
  profileEmailFontSize: '0.6875rem',
  profileAvatarSize: '32',
} as const;

/** Sidebar mobile overrides (<768px). */
export const sidebarMobileTokens = {
  headerHeight: '48px',
  headerPad: '0 10px',
  logoHeight: '24px',
  newChatFontSize: '0.8125rem',
  newChatPad: '8px 12px',
  newChatGap: '8px',
  sectionFontSize: '0.75rem',
  sectionPad: '12px 12px 4px',
  pageSectionPad: '9px 8px 4px',
  pageGroupTitlePad: '10px 8px 4px',
  sectionListPad: '0 6px',
  itemFontSize: '0.8125rem',
  itemPad: '7px 8px',
  itemGap: '8px',
  itemIconSize: '15',
  groupTitleFontSize: '0.625rem',
  groupTitlePad: '12px 6px 4px',
  historyFontSize: '0.8125rem',
  historyPad: '7px 8px',
  historyGap: '6px',
  historyIconSize: '15',
  footerPad: '6px 10px 10px',
  profilePad: '6px',
  profileGap: '8px',
  profileNameFontSize: '0.75rem',
  profileEmailFontSize: '0.625rem',
  profileAvatarSize: '28',
} as const;

export type MarkdownTokens = typeof markdownTokens;
export type MarkdownMobileTokens = typeof markdownMobileTokens;
export type BubbleTokens = typeof bubbleTokens;
export type BubbleMobileTokens = typeof bubbleMobileTokens;
export type ModalTokens = typeof modalTokens;
export type ModalMobileTokens = typeof modalMobileTokens;
export type InputTokens = typeof inputTokens;
export type InputMobileTokens = typeof inputMobileTokens;
export type DmTokens = typeof dmTokens;
export type DmMobileTokens = typeof dmMobileTokens;
export type SidebarTokens = typeof sidebarTokens;
export type SidebarMobileTokens = typeof sidebarMobileTokens;

// Restored B2C API and type contracts.
export const authTokens = {
  heroTitle: '2.4rem',
  heroDesc: '1rem',
  typing: '0.85rem',
  googleButton: '1.06rem',
  googleButtonWidth: '320px',
  googleButtonHeight: '56px',
  logoSize: '120px',
  logoRadius: '28px',
  sectionTitle: '1.75rem',
  sectionSub: '0.9rem',
  legal: '0.78rem',
  tooltip: '0.75rem',
} as const;

export const authMobileTokens = {
  heroTitle: '1.75rem',
  heroDesc: '0.9rem',
  typing: '0.75rem',
  googleButton: '0.95rem',
  googleButtonWidth: '240px',
  googleButtonHeight: '50px',
  logoSize: '78px',
  logoRadius: '20px',
  sectionTitle: '1.3rem',
  sectionSub: '0.82rem',
  legal: '0.6rem',
  tooltip: '0.72rem',
} as const;

export type AuthTokens = typeof authTokens;

export type AuthMobileTokens = typeof authMobileTokens;
